// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { useState, useEffect, useRef, SetStateAction, Dispatch } from 'react';
import * as styles from '../styles/OpenAiWidget.styles';
import { Call } from '@azure/communication-calling';
import { connectCallAutomationToGroupCall } from '../utils/callAutomationUtils';
import {
  CallProvider,
  DeclarativeCallAgent,
  StatefulCallClient,
  createStatefulCallClient,
  CallAgentProvider,
  CallClientProvider
} from '@azure/communication-react';
import { OpenAiWidgetCallScreen } from './OpenAiWidgetCallScreen';
import { v1 as createGUID } from 'uuid';
import { VideoIcon } from './constants';
import { AzureCommunicationTokenCredential, CommunicationUserIdentifier } from '@azure/communication-common';
import { AppConfigModel, fetchConfig, fetchTokenResponse } from '../utils/AppUtils';
import { InterpretingSignalsView } from './InterpretingSignalsView';
import { SkillProfileView } from './SkillProfileView';
import { LearningPathsView } from './LearningPathsView';
import { reportAgentInteraction } from '../utils/frontendTracing';

type AgentMessage = {
  id: string;
  text: string;
  timestamp: string;
  sender: 'agent' | 'user';
};

type AssessmentResult = {
  is_completed?: number;
  assessedLevel: number; // 1-5
  levelLabel: string;
  overallScore: number; // Overall score percentage (0-100)
  levelScores?: { level: number; score: number; percentage: number }[];
  keyStrengthsHeader?: string[];
  keyStrengths: string[];
  conceptGapsHeader?: string[];
  conceptGaps: string[];
  generatedAt: string;
};

export const OpenAiWidget = (props: {
  setIsOpenCallback: Dispatch<SetStateAction<boolean>>;
  /** When true, start the interview automatically when callAgent is ready (same as clicking "Start the interview now"). */
  autoStartInterview?: boolean;
  role?: string;
  level?: string;
  /** Dimension to be Assessed selected on screen 1 (Assessment Initiation). */
  dimensionToBeAssessed?: string;
  userEmail?: string;
  userName?: string;
}): JSX.Element => {
  const {
    setIsOpenCallback,
    autoStartInterview = true,
    role,
    level,
    dimensionToBeAssessed,
    userEmail,
    userName
  } = props;
  const [callAgent, setCallAgent] = useState<DeclarativeCallAgent>();
  const [statefulCallClient, setStatefulCallClient] = useState<StatefulCallClient>();
  const [tokenCredential, setTokenCredential] = useState<AzureCommunicationTokenCredential>();
  const [userIdentifier, setUserIdentifier] = useState<CommunicationUserIdentifier>();

  const [isAgentSpeakingViewActive, setIsAgentSpeakingViewActive] = useState(false); // Renamed for clarity
  const [isLoading, setIsLoading] = useState(false);
  const [currentCall, setCurrentCall] = useState<Call | undefined>(undefined);
  const [clientConfig, setClientConfig] = useState<AppConfigModel>();
  const callAutomationStarted = useRef(false);
  const [agentMessages, setAgentMessages] = useState<AgentMessage[]>([]);
  const [serverCallId, setServerCallId] = useState<string | undefined>(undefined);
  const [assessment, setAssessment] = useState<AssessmentResult | undefined>(undefined);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState<string | undefined>(undefined);
  const assessmentRequested = useRef<string | null>(null);
  const autoStartInitiated = useRef(false);
  type PostInterviewPhase = 'interpreting' | 'skill-profile' | 'learning-paths' | null;
  const [postInterviewPhase, setPostInterviewPhase] = useState<PostInterviewPhase>(null);
  const interpretingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [phoneInput, setPhoneInput] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const config = await fetchConfig();
        setClientConfig(config);
      } catch (error) {
        console.error('Error fetching configuration:', error);
      }
    };
    fetchData();
  }, []);

  // Effect for Server-Sent Events
  useEffect(() => {
    if (!currentCall || !clientConfig || !serverCallId) {
      return;
    }
    // Include serverCallId in query parameter for session isolation
    const eventSource = new EventSource(
      `${clientConfig?.callbackUri}/api/agentStatusEvents?serverCallId=${encodeURIComponent(serverCallId)}`
    );
    console.log('SSE: Connecting to server for agent status events, serverCallId:', serverCallId);
    eventSource.onmessage = (event) => {
      console.log('SSE: Received message from server:', event.data);
      try {
        const serverEvent = JSON.parse(event.data);
        console.log('SSE: EventSource URL:', serverEvent);
        if (serverEvent.type === 'agentConnectedStatus') {
          console.log('SSE: Agent connected status from server:', serverEvent.data);
          const { isConnected } = serverEvent.data;
          callAutomationStarted.current = !!isConnected;
          setIsLoading(!isConnected);
        } else if (serverEvent.type === 'aiTranscriptDelta') {
          // Real-time incremental text updates as AI speaks
          const delta = serverEvent.data?.delta as string | undefined;
          const messageId = serverEvent.data?.messageId as string | undefined;
          const timestamp = (serverEvent.data?.timestamp as string | undefined) ?? new Date().toISOString();

          if (delta && messageId) {
            setAgentMessages((prev) => {
              // Check if message with this ID already exists
              const existingIndex = prev.findIndex((m) => m.id === messageId);

              if (existingIndex >= 0) {
                // Append delta to existing message for real-time text buildup
                const updated = [...prev];
                updated[existingIndex] = {
                  ...updated[existingIndex],
                  text: updated[existingIndex].text + delta
                };
                return updated;
              } else {
                // Create new message with first delta
                return [...prev, { id: messageId, text: delta, timestamp, sender: 'agent' }];
              }
            });
          }
        } else if (serverEvent.type === 'aiTranscriptComplete') {
          // Finalize the message with complete transcript
          const transcript = serverEvent.data?.transcript as string | undefined;
          const messageId = serverEvent.data?.messageId as string | undefined;

          if (messageId && transcript) {
            setAgentMessages((prev) => {
              const existingIndex = prev.findIndex((m) => m.id === messageId);

              if (existingIndex >= 0) {
                // Update with final complete transcript (ensures accuracy)
                const updated = [...prev];
                updated[existingIndex] = {
                  ...updated[existingIndex],
                  text: transcript
                };
                return updated;
              } else {
                // Fallback: create message if deltas were somehow missed
                const timestamp = (serverEvent.data?.timestamp as string | undefined) ?? new Date().toISOString();
                console.log('[SSE] Creating message from aiTranscriptComplete (deltas may have been missed)');
                return [...prev, { id: messageId, text: transcript, timestamp, sender: 'agent' }];
              }
            });
          }
        } else if (serverEvent.type === 'aiTranscript') {
          // Legacy event - kept for backward compatibility but should not be used when delta events work
          const transcript = serverEvent.data?.transcript as string | undefined;
          const timestamp = (serverEvent.data?.timestamp as string | undefined) ?? new Date().toISOString();
          if (transcript) {
            console.log('[SSE] Received legacy aiTranscript event (delta events may not be working)');
            setAgentMessages((prev) => {
              // Only add if this exact message doesn't already exist (from delta events)
              const alreadyExists = prev.some((m) => m.text === transcript && m.sender === 'agent');
              if (!alreadyExists) {
                return [...prev, { id: `${Date.now()}-${prev.length}`, text: transcript, timestamp, sender: 'agent' }];
              }
              return prev;
            });
          }
        } else if (serverEvent.type === 'userTranscript') {
          const transcript = serverEvent.data?.transcript as string | undefined;
          const timestamp = (serverEvent.data?.timestamp as string | undefined) ?? new Date().toISOString();
          if (transcript) {
            console.log('User transcript from server:', transcript);
            setAgentMessages((prev) => [
              ...prev,
              { id: `${Date.now()}-${prev.length}`, text: transcript, timestamp, sender: 'user' }
            ]);
          }
        }
      } catch (error) {
        console.error('SSE: Error parsing message data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE: EventSource failed:', error);
    };

    return () => {
      console.log('SSE: Closing EventSource connection.');
      eventSource.close();
    };
  }, [currentCall, clientConfig, serverCallId]);

  const simulateServerRequest = (): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(true);
      }, 1500);
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { token, user } = await fetchTokenResponse();
        console.log('Token fetched: ', token);
        console.log('User fetched: ', user);
        setUserIdentifier(user);
        setTokenCredential(new AzureCommunicationTokenCredential(token));
      } catch (e) {
        console.error(e);
      }
    })();
  }, [setUserIdentifier]);

  useEffect(() => {
    if (statefulCallClient === undefined) {
      if (!userIdentifier) {
        return;
      }
      const statefulClient = createStatefulCallClient({ userId: userIdentifier });
      setStatefulCallClient(statefulClient);
      console.log('Stateful client created', statefulClient);
    }
  }, [userIdentifier, setStatefulCallClient, statefulCallClient]);

  useEffect(() => {
    if (callAgent === undefined && statefulCallClient && tokenCredential) {
      const createCallAgent = async (): Promise<void> => {
        const callAgent = await statefulCallClient.createCallAgent(tokenCredential);
        setCallAgent(callAgent);
      };
      createCallAgent();
    }
  }, [callAgent, statefulCallClient, tokenCredential]);

  useEffect(() => {
    if (!currentCall) {
      return;
    }

    const handleStateChange = async (): Promise<void> => {
      if (currentCall.state === 'Connected') {
        console.log('CALL STATE:', currentCall);
        setIsLoading(false);
        setIsAgentSpeakingViewActive(true);
        if (!callAutomationStarted.current) {
          callAutomationStarted.current = true;
          console.log('Connecting to call automation...');
          try {
            const id = await currentCall.info?.getServerCallId();
            if (id) {
              setServerCallId(id);

              // STEP 1: Generate dynamic prompt with serverCallId (for session isolation)
              if (role && level) {
                const selfAssessedLevel = parseInt(level, 10);
                // Trace exactly what the frontend actually sends over the wire - no
                // backend-aware filtering/curation, since the frontend has no notion of
                // which of its own fields are "backend internals".
                const promptRequestPayload = {
                  functionalArea: role,
                  selfAssessedLevel,
                  serverCallId: id,
                  emailId: userEmail,
                  teamMemberName: userName,
                  assessmentMode: 'Coach'
                };
                const startTime = Date.now();
                try {
                  const promptResponse = await fetch('/api/generateDynamicPrompt', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(promptRequestPayload)
                  });

                  if (promptResponse.ok) {
                    const promptData = await promptResponse.json();
                    console.log('✅ Dynamic prompt generated and stored with serverCallId');
                    console.log('✅ Assessment record created with ID:', promptData.assessmentId);
                    reportAgentInteraction({
                      sessionId: id,
                      callType: 'start-interview',
                      request: promptRequestPayload,
                      response: promptData,
                      status: 'success',
                      startTime,
                      endTime: Date.now()
                    });
                  } else {
                    console.error('⚠️ Failed to generate dynamic prompt:', promptResponse.statusText);
                    reportAgentInteraction({
                      sessionId: id,
                      callType: 'start-interview',
                      request: promptRequestPayload,
                      status: 'error',
                      errorMessage: `Failed to generate dynamic prompt: ${promptResponse.statusText}`,
                      startTime,
                      endTime: Date.now()
                    });
                  }
                } catch (promptError) {
                  console.error('⚠️ Error generating dynamic prompt:', promptError);
                  reportAgentInteraction({
                    sessionId: id,
                    callType: 'start-interview',
                    request: promptRequestPayload,
                    status: 'error',
                    errorMessage: promptError instanceof Error ? promptError.message : String(promptError),
                    startTime,
                    endTime: Date.now()
                  });
                  // Don't fail the call if prompt generation fails - will use default
                }
              }
            }
            await connectCallAutomationToGroupCall(currentCall);
            console.log('Successfully connected to call automation.');
          } catch (e) {
            console.error('Error connecting to call automation:', e);
            callAutomationStarted.current = false;
          }
        }
      }

      if (currentCall.state === 'Disconnected') {
        console.log('Call ended, resetting state.');
        setIsAgentSpeakingViewActive(false);
        setAgentMessages([]);
        callAutomationStarted.current = false;
        setCurrentCall(undefined);
        setIsLoading(false);
        const id = serverCallId ?? (await currentCall.info?.getServerCallId());
        if (id && assessmentRequested.current !== id) {
          assessmentRequested.current = id;
          void fetchAssessment(id);
        }
        setPostInterviewPhase('interpreting');
        interpretingTimerRef.current = setTimeout(() => {
          setPostInterviewPhase('skill-profile');
          interpretingTimerRef.current = null;
        }, 6 * 1000);
      }
      console.log('Current call state:', currentCall.state);
    };

    currentCall.on('stateChanged', handleStateChange);
    return () => currentCall.off('stateChanged', handleStateChange);
  }, [currentCall, serverCallId]);

  // When opened via "Start Assessment", start the interview automatically (same as "Start the interview now").
  useEffect(() => {
    if (
      autoStartInterview &&
      callAgent &&
      !currentCall &&
      !isLoading &&
      !assessment &&
      !assessmentLoading &&
      !assessmentError &&
      !autoStartInitiated.current
    ) {
      autoStartInitiated.current = true;
      void handleGetAdviceClick();
    }
  }, [autoStartInterview, callAgent, currentCall, isLoading, assessment, assessmentLoading, assessmentError]);

  const resolvePhoneNumber = (): string | null => {
    const normalized = phoneInput.trim() || '';
    // if (!normalized) {
    //   setPhoneError('Please enter a phone number to start the call.');
    //   return null;
    // }
    // setPhoneError(null);
    return normalized;
  };

  const handleGetAdviceClick = async (): Promise<void> => {
    if (!callAgent) return;
    console.log('Starting call with agent...');

    resetForNewCall();
    setAgentMessages([]);
    // TODO: replace with callAgent?.startCall to make a phoneCall
    // const call = agent.startCall(
    //   [{ phoneNumber: "<to_phone_number>" }],
    //   { alternateCallerId: { phoneNumber: "<from_phone_number>" } }
    // );
    const callTemp = callAgent?.join({ groupId: createGUID() });
    setCurrentCall(callTemp);
    setIsLoading(true);
    const success = await simulateServerRequest();
    console.log('Successfully created a call');
    console.log('StatefulCallClient:', statefulCallClient);
    console.log('CallAgent:', callAgent);
    if (success) {
      setIsAgentSpeakingViewActive(true); // Activate the agent speaking view
    } else {
      alert("Sorry, we couldn't connect to the expert right now.");
    }
  };

  const fetchAssessment = async (id: string): Promise<void> => {
    setAssessmentLoading(true);
    setAssessmentError(undefined);
    const startTime = Date.now();
    try {
      const response = await fetch(`/api/assessment/${encodeURIComponent(id)}`);
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch assessment: ${response.statusText}`);
      }
      const result: AssessmentResult = await response.json();
      setAssessment(result);
      reportAgentInteraction({
        sessionId: id,
        callType: 'get-assessment',
        request: { serverCallId: id },
        response: result,
        status: 'success',
        startTime,
        endTime: Date.now()
      });
    } catch (error: any) {
      console.error(error);
      setAssessmentError('Could not generate assessment yet. Please try again in a moment.');
      reportAgentInteraction({
        sessionId: id,
        callType: 'get-assessment',
        request: { serverCallId: id },
        status: 'error',
        errorMessage: error instanceof Error ? error.message : String(error),
        startTime,
        endTime: Date.now()
      });
    } finally {
      setAssessmentLoading(false);
    }
  };

  const closeAll = (): void => {
    setIsAgentSpeakingViewActive(false);
    setAgentMessages([]);
  };

  const resetForNewCall = (): void => {
    if (interpretingTimerRef.current) {
      clearTimeout(interpretingTimerRef.current);
      interpretingTimerRef.current = null;
    }
    setPostInterviewPhase(null);
    setAssessment(undefined);
    setAssessmentError(undefined);
    setServerCallId(undefined);
    assessmentRequested.current = null;
    setIsLoading(false);
    setIsAgentSpeakingViewActive(false);
    setAgentMessages([]);
    setCurrentCall(undefined);
    callAutomationStarted.current = false;
  };

  if (isAgentSpeakingViewActive && statefulCallClient && currentCall && callAgent && !isLoading) {
    console.log({
      isAgentSpeakingViewActive,
      isLoading,
      currentCall: !!currentCall,
      callAgent: !!callAgent,
      statefulCallClient: !!statefulCallClient
    });
    return (
      <CallClientProvider callClient={statefulCallClient}>
        <CallAgentProvider callAgent={callAgent}>
          <CallProvider call={currentCall}>
            <OpenAiWidgetCallScreen currentCall={currentCall} onHangup={closeAll} messages={agentMessages} />
          </CallProvider>
        </CallAgentProvider>
      </CallClientProvider>
    );
  }

  console.log('POST INTERVIEW PHASE', postInterviewPhase);

  if (postInterviewPhase === 'interpreting') {
    return (
      <div style={{ width: '100%' }}>
        <InterpretingSignalsView />
      </div>
    );
  }

  if (postInterviewPhase === 'skill-profile') {
    return (
      <div style={{ width: '100%' }}>
        <SkillProfileView
          assessment={assessment}
          loading={assessmentLoading}
          error={assessmentError}
          dimensionToBeAssessed={dimensionToBeAssessed}
          onViewLearningPaths={() => setPostInterviewPhase('learning-paths')}
          onStartNewInterview={() => {
            resetForNewCall();
            handleGetAdviceClick();
          }}
          serverCallId={serverCallId}
          userEmail={userEmail}
          onGoBack={() => {
            resetForNewCall();
            setIsOpenCallback(false);
          }}
        />
      </div>
    );
  }

  if (postInterviewPhase === 'learning-paths') {
    return (
      <div style={{ width: '100%' }}>
        <LearningPathsView
          onBackToSkillProfile={() => setPostInterviewPhase('skill-profile')}
          assessment={assessment}
          role={role}
          level={level}
          serverCallId={serverCallId}
          userEmail={userEmail}
        />
      </div>
    );
  }

  function setIsOpen(isOpen: boolean): void {
    throw new Error('Function not implemented.');
  }

  console.log('Rendering OpenAiWidget: isLoading:', isLoading, 'callAgent:', !!callAgent);

  // Default: "Talk to an expert" card
  return (
    <div style={styles.expertCardStyle}>
      <button
        style={{
          ...styles.getAdviceButtonStyle,
          ...(isLoading && styles.getAdviceButtonLoadingStyle),
          ...(!callAgent && styles.getAdviceButtonDisabledStyle),
          ...((isLoading || !callAgent) && {
            backgroundColor: 'transparent',
            color: '#2a312b',
            fontSize: 24
          })
        }}
        onClick={handleGetAdviceClick}
        disabled={!callAgent || isLoading}
      >
        {isLoading ? (
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
              color: '#2a312b',
              fontSize: 24
            }}
          >
            Connecting...
            <div className="openai-widget-button-loader" aria-hidden="true" />
          </span>
        ) : !callAgent ? (
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
              color: '#2a312b',
              fontSize: 24
            }}
          >
            Initializing...
            <div className="openai-widget-button-loader" aria-hidden="true" />
          </span>
        ) : (
          <>
            <VideoIcon /> Start the interview now
          </>
        )}
      </button>
    </div>
  );
};
